import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import {
  PHOTO_CONSENT_HINT,
  uploadActivityPhoto,
  type ActivityReportSource,
} from "@/lib/activity-reports";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export function ActivityReportDialog({
  open,
  onOpenChange,
  sourceType,
  sourceId,
  title = "Поделись фото",
  onPublished,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  sourceType: ActivityReportSource;
  sourceId: string;
  title?: string;
  onPublished?: () => void;
}) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);

  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !user) return;
    setUploading(true);
    try {
      setPhotoUrl(await uploadActivityPhoto(file, user.id));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Не удалось загрузить фото");
    } finally {
      setUploading(false);
    }
  }

  const publish = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Нужно войти");
      if (!photoUrl) throw new Error("Сначала загрузи фото");
      const { error } = await supabase.from("activity_reports").insert({
        author_user_id: user.id,
        source_type: sourceType,
        source_id: sourceId,
        photo_url: photoUrl,
        caption: caption.trim() || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Фотоотчёт опубликован");
      qc.invalidateQueries({ queryKey: ["activity-reports"] });
      setPhotoUrl(null);
      setCaption("");
      onOpenChange(false);
      onPublished?.();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {photoUrl ? (
          <div className="flex max-h-[70vh] w-full items-center justify-center overflow-hidden rounded-2xl bg-td-dark">
            <img loading="lazy" decoding="async"
              src={photoUrl}
              alt="Фото отчёта"
              className="h-auto max-h-[70vh] w-full object-contain"
            />
          </div>
        ) : (
          <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-td-line bg-td-soft/40 p-8 text-center text-sm text-td-muted hover:border-td-teal">
            {uploading ? (
              <Loader2 className="h-6 w-6 animate-spin text-td-teal" />
            ) : (
              <ImagePlus className="h-6 w-6 text-td-teal" />
            )}
            <span>{uploading ? "Загружаем…" : "Выбрать фото"}</span>
            <input type="file" accept="image/*" className="hidden" onChange={pick} />
          </label>
        )}
        {photoUrl && (
          <p className="text-xs text-td-muted">
            В ленте фото покажется целиком в горизонтальном кадре
          </p>
        )}

        <Textarea
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          rows={3}
          maxLength={280}
          placeholder="Короткая подпись (необязательно)"
        />
        <p className="text-xs text-td-muted">{PHOTO_CONSENT_HINT}</p>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Пропустить
          </Button>
          <Button
            className="cta-solid"
            disabled={!photoUrl || publish.isPending}
            onClick={() => publish.mutate()}
          >
            Опубликовать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
